import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap } from 'lucide-react';
import FloatingChips from '../components/FloatingChips';
import { FLAVOR_LIST, MYSTERY, TAGLINE, SPECS, scrollToId } from '../lib/products';
import { useNavigate } from 'react-router-dom';
import KrispButton from '../components/KrispButton';

const SLIDES = [...FLAVOR_LIST, MYSTERY];

export default function HeroSlider() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();
  const f = SLIDES[i];
  const n = SLIDES.length;
  const mystery = !!f.soon;

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setI((v) => (v + 1) % n), 7500);
    return () => clearInterval(id);
  }, [i, n, paused]);

  return (
    <section id="hero" data-testid="hero-slider" className="relative overflow-hidden transition-colors duration-700 min-h-[82vh]" style={{ background: f.tint }}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="absolute inset-x-0 top-6 md:top-2 overflow-hidden select-none pointer-events-none" aria-hidden="true">
        <p className="font-display text-[34vw] md:text-[22vw] leading-[0.8] whitespace-nowrap -ml-[6vw] text-cocoa/[0.06]">{TAGLINE} {TAGLINE}</p>
      </div>
      <div className="absolute -bottom-40 -right-32 w-[46vw] h-[46vw] rounded-full opacity-40 transition-colors duration-700" style={{ background: f.color }} />
      <div className="absolute inset-y-0 left-0 w-[6px] md:w-[10px] transition-colors duration-700" style={{ background: f.color }} />
      <FloatingChips edges />

      <div className="relative max-w-[1500px] mx-auto px-5 md:px-14 pt-10 md:pt-16 pb-12 grid md:grid-cols-[110px_1fr_1fr] gap-6 md:gap-8 items-center min-h-[82vh]">
        <div data-testid="hero-flavour-switcher" className="flex md:flex-col gap-3 md:gap-4 justify-center order-2 md:order-1">
          {SLIDES.map((s, k) => (
            <button key={s.slug} data-testid={`hero-flavour-${s.slug}`} data-cursor="plus" aria-label={s.name} onClick={() => setI(k)}
              className={`relative w-20 h-20 md:w-24 md:h-24 rounded-2xl border-2 border-cocoa p-1.5 flex items-center justify-center transition-all duration-300 ${k === i ? 'bg-cocoa shadow-[4px_4px_0_#F7B500] -translate-y-1' : 'bg-cream/70 hover:bg-cream'}`}>
              <img src={s.img50} alt={s.name} className="h-full object-contain" />
              {k === i && <span className="absolute -right-2 -top-2 w-5 h-5 rounded-full bg-kyellow border-2 border-cocoa" />}
            </button>
          ))}
        </div>

        <div className="relative order-1 md:order-2 h-[42vh] md:h-[62vh] flex items-center justify-center" data-cursor="expand" data-cursor-color={f.color}>
          <AnimatePresence mode="wait">
            <motion.div key={f.slug} className="relative h-full w-full flex items-center justify-center gap-1 overflow-hidden"
              initial={{ opacity: 0, y: 60, rotate: -10 }} animate={{ opacity: 1, y: 0, rotate: 0 }} exit={{ opacity: 0, y: -50, rotate: 8 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
              <img src={f.img50} alt={`PROKRISP ${f.name} 50G`} data-testid={mystery ? 'hero-mystery' : 'hero-pack'} className="h-full max-w-full object-contain gpu -rotate-3" />
            </motion.div>
          </AnimatePresence>
          <motion.span
            className="absolute top-0 left-0 z-30 -rotate-6 rounded-xl bg-cocoa text-kyellow font-display text-xl md:text-2xl px-5 py-2 shadow-[4px_4px_0_#E11D24] flex items-center gap-2">
            <Zap size={18} className="fill-kyellow" /> {mystery ? 'STAY TUNED' : '15G PROTEIN'}
          </motion.span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={f.slug} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.45 }}
            className="relative z-10 order-3 text-center md:text-left">
            <p className="font-cond font-bold text-base md:text-xl tracking-[0.35em] text-cocoa/70">{mystery ? 'COMING SOON · STAY TUNED' : 'NOW LIVE · 50G · ₹50'} · {String(i + 1).padStart(2, '0')}/{String(n).padStart(2, '0')}</p>
            <h1 data-testid="hero-title" className="font-display leading-[0.86] text-[17vw] md:text-[7.5vw] text-cocoa mt-2">
              {f.name.split(' ').map((w, k) => <span key={k} className="block" style={k === 1 && !mystery ? { color: f.color } : {}}>{w}</span>)}
            </h1>
            <p className="font-cond font-semibold text-sm md:text-lg tracking-[0.25em] uppercase mt-3 text-cocoa/80">{f.hook}</p>
            <p className="text-sm md:text-base text-cocoa/70 mt-3 max-w-md mx-auto md:mx-0">
              {mystery ? 'Salt & Pepper, Tomato, Sweet Chilli or Pudina — the crowd picks the third flavour. Cast your vote and go beyond.' : '15g protein in every 50g pack. Roasted, plant based, no palm oil, no artificial colours. Minimum order 3 packets — shipped anywhere in India.'}
            </p>
            <div className="flex flex-wrap gap-2 mt-4 justify-center md:justify-start">
              {SPECS.slice(0, 4).map((s) => <span key={s} className="rounded-full border-2 border-cocoa/30 px-3 py-1 text-[9px] font-black tracking-[0.2em] text-cocoa/70">{s}</span>)}
            </div>
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-7">
              {mystery ? (
                <Link to="/vote"><KrispButton testid="hero-vote">VOTE FOR FLAVOUR #3 →</KrispButton></Link>
              ) : (
                <KrispButton testid="hero-order-now" color={f.color} onClick={() => navigate(`/checkout?add=${f.slug}`)}>ORDER NOW · ₹50</KrispButton>
              )}
              <KrispButton testid="hero-explore" variant="ghost" onClick={() => scrollToId('lineup')}>SEE THE LINEUP ↓</KrispButton>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
